// Ops (spec §6.8): feature flags. Backed by the Task-23 addition
// admin-flags (see that function's header for why it's needed — the AI
// quota view and general "incident playbook shortcuts" are covered by the
// Hosts screen's per-business incident button (spec §6.4/§6.8 overlap) and
// the AI planner is out of scope for this build (plan.md §10).
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';
import { adminMutate } from '@/lib/api/adminMutate';
import { toReason } from '@/lib/api/reason';
import { cancelThenInvalidate } from '@/lib/query/refresh';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/ops')({
  component: OpsPage,
});

interface Flag {
  key: string;
  enabled: boolean;
  clientVisible: boolean;
  startsAt: string | null;
  endsAt: string | null;
  config: Record<string, unknown>;
  description: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

function useFlags() {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-flags', 'list'],
    queryFn: () => callEdgeFunction<{ flags: Flag[] }>(supabase, 'admin-flags', { action: 'list' }),
  });
}

function OpsPage() {
  const { supabase } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useFlags();
  const [reason, setReason] = useState('');

  const toggleMutation = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      const r = toReason(reason);
      if (!r) throw new Error('A reason of at least 10 characters is required.');
      return adminMutate(supabase, 'admin-flags', { action: 'update', key, enabled }, r);
    },
    onSuccess: () => {
      setReason('');
      cancelThenInvalidate(queryClient, { queryKey: ['admin-flags', 'list'] });
    },
  });

  if (isLoading) return <PageLoading />;
  if (error || !data) return <p className="text-body text-danger">Couldn't load flags: {(error as Error)?.message}</p>;

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Ops</h1>
        <p className="mt-sm text-body text-ink-2">
          Platform feature flags. A flag never unlocks a paid feature by itself — the server re-checks it on
          every path it gates. Incident shortcuts (freeze a host's rails, notify recent payers) live on that
          host's page under Hosts.
        </p>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <label className="text-caption font-semibold text-ink-2" htmlFor="flag-reason">
          Reason (required for every flag change)
        </label>
        <textarea
          id="flag-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-xs min-h-[52px] w-full rounded-button border border-border bg-surface p-sm text-body text-ink"
        />
      </div>

      <div className="flex flex-col gap-sm">
        {data.flags.map((flag) => (
          <div key={flag.key} className="rounded-card border border-border bg-surface p-lg shadow-card">
            <div className="flex items-center justify-between gap-md">
              <div>
                <h2 className="text-h3 font-bold text-ink">{flag.key}</h2>
                {flag.description ? <p className="mt-xs text-caption text-ink-2">{flag.description}</p> : null}
                {flag.startsAt || flag.endsAt ? (
                  <p className="mt-xs text-caption text-ink-2">
                    Window: {flag.startsAt ? new Date(flag.startsAt).toLocaleDateString() : '—'} –{' '}
                    {flag.endsAt ? new Date(flag.endsAt).toLocaleDateString() : '—'}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                disabled={toggleMutation.isPending || !toReason(reason)}
                onClick={() => toggleMutation.mutate({ key: flag.key, enabled: !flag.enabled })}
                className={`rounded-button px-md py-sm text-body font-semibold disabled:opacity-50 ${
                  flag.enabled ? 'bg-canyon text-white' : 'border border-border text-ink'
                }`}
              >
                {flag.enabled ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {toggleMutation.isError ? <p className="text-body text-danger">{(toggleMutation.error as Error).message}</p> : null}
    </section>
  );
}
